// The waits of the stores (R3258, ui-map-interface.md §8 "Specification
// rows" and §12.0): how long the empire's own rates take to fill a store,
// and how long they take to close the shortfall of a price. A BUILD row
// short of stores states the wait in the time slot of its cost column,
// its card states the whole sentence, and the top-bar store card states
// the time to a full store. The arcade's `shortfall` (`arcade/format.ts`)
// is the same shape for the one currency of a match.
//
// Every rate is per GAME hour, so every wait is in game seconds and goes
// through `dur` of `state.ts`, as the build time beside it does.

import type { Resources } from '../gen/proto/aquaphobia';
import { type GameState, RES_ORDER } from '../state';

/** The rate of each store per game hour: the mines of the income for
 *  alloy, biomass and volatiles, the idle mint for credits (R355), and 0
 *  for isotopes, which a dive salvages and no mine yields. */
export function storeRates(s: GameState): Resources {
  const inc = s.empire?.income;
  return {
    alloy: inc?.alloy ?? 0,
    biomass: inc?.biomass ?? 0,
    volatiles: inc?.volatiles ?? 0,
    isotopes: 0,
    credits: s.rules.idleCreditsPerHour,
  };
}

/** The game seconds until a store of `have` fills its `cap` at `ratePerHour`,
 *  or undefined for a store with no cap, no rate or no room left. */
export function fullInSec(have: number, cap: number, ratePerHour: number): number | undefined {
  if (!(cap > 0) || !(ratePerHour > 0) || have >= cap) return undefined;
  return ((cap - have) / ratePerHour) * 3600;
}

/** What the stores lack of a price: each part the stores cannot pay, by
 *  the missing amount, and 0 for a part they cover. */
export function shortOf(cost: Resources, have: Resources): Resources {
  const out = { alloy: 0, biomass: 0, volatiles: 0, isotopes: 0, credits: 0 };
  for (const k of RES_ORDER) out[k] = Math.max(0, Math.ceil(cost[k] - have[k]));
  return out;
}

/**
 * The game seconds until the rates close the shortfall of a price, or
 * undefined when the stores pay it now or a short part has no rate: the
 * isotopes, or a mine that yields nothing. A wait that cannot end is
 * left out rather than printed as an infinity, as the arcade's
 * `shortfall` leaves it out.
 */
export function shortWaitSec(cost: Resources, have: Resources, rates: Resources): number | undefined {
  const miss = shortOf(cost, have);
  let wait = 0;
  let any = false;
  for (const k of RES_ORDER) {
    if (miss[k] <= 0) continue;
    if (!(rates[k] > 0)) return undefined;
    any = true;
    wait = Math.max(wait, (miss[k] / rates[k]) * 3600);
  }
  return any ? wait : undefined;
}
